import { clsx } from 'clsx'
import { type ComponentPropsWithoutRef, type ReactElement } from 'react'

import type {
  TextSize,
  TitleSize,
  TypeComponentTitleDescriptionBlockEntry,
} from '@contentful-types/component-title-description-block'

import { AssetRenderer } from '@components/renderer/asset'
import { renderRichTextMultiLineContent } from '@components/renderer/rich-text'
import { Column } from '@components/ui-core/column/column'
import { Text, type TextVariant } from '@components/ui-core/text/text'

import styles from './title-description-block.module.css'

interface TitleDescriptionBlockProps extends ComponentPropsWithoutRef<'div'> {
  data: TypeComponentTitleDescriptionBlockEntry
  titleTag?: 'h1' | 'h2'
}

export function TitleDescriptionBlock({
  className,
  data,
  titleTag,
}: TitleDescriptionBlockProps): ReactElement {
  const {
    fields: {
      iconAsset,
      title,
      titleSize,
      description,
      descriptionSize,
      eyebrowTitle,
      eyebrowTitleSize,
    },
  } = data

  const headingSizeVariant: Record<TitleSize, TextVariant> = {
    Jumbo2: 'jumbo2',
    Jumbo1: 'jumbo1',
    'H.XL': 'headerXlarge',
    'H.L': 'headerLarge',
    'H.M': 'headerMedium',
    'H.S': 'headerSmall',
  }

  const textSizeVariant: Record<TextSize, TextVariant> = {
    'T.XL': 'bodyXlarge',
    'T.L': 'bodyLarge',
    'T.M': 'bodyMedium',
    'T.S': 'bodySmall',
  }

  const hasIcon = !!iconAsset?.fields?.asset?.fields?.file?.url

  return (
    <Column className={clsx(styles.content, className)} gap="6x">
      {hasIcon && <AssetRenderer data={iconAsset} />}
      {eyebrowTitle && (
        <Text as="span" variant={textSizeVariant[eyebrowTitleSize ?? 'T.M']}>
          {renderRichTextMultiLineContent(eyebrowTitle)}
        </Text>
      )}
      {title && (
        <Text as={titleTag} variant={headingSizeVariant[titleSize ?? 'H.L']}>
          {renderRichTextMultiLineContent(title)}
        </Text>
      )}
      {description && (
        <Text as="span" variant={textSizeVariant[descriptionSize ?? 'T.M']}>
          {renderRichTextMultiLineContent(description)}
        </Text>
      )}
    </Column>
  )
}
